import type React from "react";
import { createContext, type Dispatch, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentFlags } from "../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../hooks/useErrorSnackbar";
import { useOpenShifts } from "../hooks/useOpenShifts";
import { mergeGroupKey } from "../utils/incentive";
import {
	NotificationType,
	type OpenShift,
	type OpenShiftRequest,
	type OpenShiftType,
	type Request,
	RequestSource,
	type ShiftRequest,
	type ShiftType,
} from "../utils/models";
import { isBedSideNurse, isHeadNurse } from "../utils/utilFunctions";
import { type Department, useAuth } from "./AuthProvider";
import { useChosenDepartment } from "./ChosenDepartment";

interface RequestsContextType {
	staffRequests: ShiftRequest[];
	myRequests: ShiftRequest[];
	refreshRequests: () => Promise<void>;
	insertRequest: (request: OpenShiftRequest) => void;
	setStaffRequests: Dispatch<React.SetStateAction<ShiftRequest[]>>;
	mergedRequests: OpenShiftType[];
}

const RequestsContext = createContext<RequestsContextType | null>(null);

export const RequestsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [myRequests, setMyRequests] = useState<ShiftRequest[]>([]);
	const [staffRequests, setStaffRequests] = useState<ShiftRequest[]>([]);
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { userInfo } = useAuth();

	const departmentIdForQuery = useMemo(
		() => (isHeadNurse(userInfo.role) || isMedicalDepartment ? chosenDepartment.department_id : undefined),
		[userInfo.role, chosenDepartment.department_id, isMedicalDepartment],
	);

	const { data: openShiftsData, error: openShiftsError, refetch: refetchOpenShifts } = useOpenShifts(departmentIdForQuery);

	useEffect(() => {
		if (!openShiftsError) return;
		showErrorSnackbar(t("open.shifts.load.failed"), { action: "open_shifts_load", department_id: departmentIdForQuery }, openShiftsError);
	}, [openShiftsError, showErrorSnackbar, t, departmentIdForQuery]);

	const insertRequest = (request: OpenShiftRequest) => {
		setStaffRequests([...staffRequests, request]);
		setMyRequests([...staffRequests, request]);
	};

	const refreshRequests = async (department?: Department) => {
		const targetDepartment = department || chosenDepartment;
		if (!targetDepartment?.department_id || !userInfo) return;

		try {
			const result = await refetchOpenShifts();
			const openShifts = result.data || [];

			const requestsFromShifts: ShiftRequest[] = openShifts.map((shift: Request) => ({
				id: shift.id,
				request_type: shift.request_type,
				created_at: shift.created_at,
				created_by: shift.created_by,
				replacement_nurse_id: shift.request_for || 0,
				request_for_shift: shift.request_for_shift,
				needed_nurses: shift.needed_nurses,
				status: shift.status,
				charge_only: shift.charge_only,
				bonus: shift.bonus,
				incentive_configuration_id: shift.incentive_configuration_id,
				current_bonus: shift.current_bonus,
				hours_left: shift.hours_left,
				incentive_windows: shift.incentive_windows,
				department_id: shift.department_id,
				location: shift.location,
				agency_shift_ids: shift.agency_shift_ids,
				agency_source_name: shift.agency_source_name,
				agency_post_delay_hours: shift.agency_post_delay_hours,
				agency_targets: shift.agency_targets,
				auto_close_at: shift.auto_close_at,
				auto_close_uses_agency_terms: shift.auto_close_uses_agency_terms,
				source: shift.request_type === NotificationType.OpenShift ? shift.source : null,
				job_id: shift.job_id,
				sub_role: shift.sub_role,
				// future use
				// allow_agency: shift.allow_agency,
				// allow_overtime: shift.allow_overtime,
				message: {
					date: shift.shift_start,
					reason: shift.reason || "No reason provided",
					accepted_nurses: shift.accepted_nurses || [],
					roles: shift.roles || [],
					shift_type: shift.shift_type,
					request_data: (shift.request_data as any) || [],
					shift_start: shift.shift_start,
					shift_end: shift.shift_end,
				},
			}));

			setStaffRequests(requestsFromShifts.filter((r) => r.created_by !== userInfo.id || isHeadNurse(userInfo.role)));
			setMyRequests(requestsFromShifts.filter((r) => r.created_by === userInfo.id && isBedSideNurse(userInfo.role) && r.source !== RequestSource.CallOff));
		} catch (error) {
			console.log(error);
			showErrorSnackbar(
				"Failed to fetch open shifts. Please try again later.",
				{
					action: "failed to fetch open shifts",
				},
				error,
			);
		}
	};

	useEffect(() => {
		if (!openShiftsData) return;

		try {
			const requestsFromShifts: ShiftRequest[] = openShiftsData.map<ShiftRequest>((shift: Request) => ({
				id: shift.id,
				request_type: shift.request_type,
				created_at: shift.created_at,
				created_by: shift.created_by,
				replacement_nurse_id: shift.request_for || 0,
				request_for_shift: shift.request_for_shift,
				request_target_shift: shift.request_target_shift,
				needed_nurses: shift.needed_nurses,
				status: shift.status,
				charge_only: shift.charge_only,
				bonus: shift.bonus,
				incentive_configuration_id: shift.incentive_configuration_id,
				current_bonus: shift.current_bonus,
				hours_left: shift.hours_left,
				incentive_windows: shift.incentive_windows,
				department_id: shift.department_id,
				location: shift.location,
				agency_shift_ids: shift.agency_shift_ids,
				agency_source_name: shift.agency_source_name,
				agency_post_delay_hours: shift.agency_post_delay_hours,
				agency_targets: shift.agency_targets,
				auto_close_at: shift.auto_close_at,
				auto_close_uses_agency_terms: shift.auto_close_uses_agency_terms,
				source: shift.request_type === NotificationType.OpenShift ? shift.source : null,
				job_id: shift.job_id,
				sub_role: shift.sub_role,
				message: {
					date: shift.shift_start,
					reason: shift.reason || "No reason provided",
					accepted_nurses: shift.accepted_nurses || [],
					roles: shift.roles || [],
					shift_type: shift.shift_type,
					request_data: (shift.request_data as any) || [],
					shift_start: shift.shift_start,
					shift_end: shift.shift_end,
				},
			}));

			setStaffRequests(requestsFromShifts.filter((r) => r.created_by !== userInfo.id || isHeadNurse(userInfo.role)));
			setMyRequests(requestsFromShifts.filter((r) => r.created_by === userInfo.id && isBedSideNurse(userInfo.role) && r.source !== RequestSource.CallOff));
		} catch (error) {
			showErrorSnackbar(
				"Failed to process open shifts.",
				{
					action: "failed to process open shifts",
				},
				error,
			);
		}
	}, [openShiftsData, userInfo.id, userInfo.role, showErrorSnackbar]);

	const mergedRequests = useMemo(() => {
		if (!userInfo.nurses_info || !staffRequests) return [];

		// Transform OpenShift[] to OpenShiftType[]
		const transformedShifts: OpenShiftType[] = staffRequests
			.filter((request): request is OpenShiftRequest => "needed_nurses" in request)
			.map((openShift): OpenShiftType => {
				const nurseId = userInfo.nurses_info?.find((nurseInfo) => nurseInfo.departments.includes(openShift.department_id))?.nurse.id;
				const myRequestData = openShift.message.request_data.find((r) => r.nurse_id === nurseId);
				return {
					id: openShift.id,
					startTime: openShift.message.shift_start,
					endTime: openShift.message.shift_end,
					type: openShift.message.shift_type as ShiftType.Day | ShiftType.Night,
					status: openShift.message.request_data?.[0]?.status || "",
					location: openShift.location || "",
					neededNurses: openShift.needed_nurses,
					created_by: String(openShift.created_by),
					myStatus: myRequestData?.status || "",
					accepted_start_time: myRequestData?.accepted_start_time ? myRequestData.accepted_start_time : undefined,
					accepted_end_time: myRequestData?.accepted_end_time ? myRequestData.accepted_end_time : undefined,
					charge_only: openShift.charge_only,
					bonus: openShift.bonus,
					department_id: openShift.department_id,
					job_id: openShift.job_id,
					incentive_configuration_id: openShift.incentive_configuration_id,
					current_bonus: openShift.current_bonus,
					hours_left: openShift.hours_left,
					incentive_windows: openShift.incentive_windows,
					posted_at: openShift.created_at,
					// future use
					// allow_agency: openShift.allow_agency,
					// allow_overtime: openShift.allow_overtime,
				};
			});

		// Key the merge off snapshot windows, not the legacy `shift.bonus` column.
		const groupedShifts: Record<string, OpenShiftType[]> = {};
		transformedShifts.forEach((shift) => {
			const key = `${shift.type}_${shift.startTime.toFormat("yyyy-MM-dd_HH:mm")}_${shift.endTime.toFormat("yyyy-MM-dd_HH:mm")}_${shift.status}_${mergeGroupKey(shift.id, shift.incentive_windows)}_${shift.job_id ?? ""}_${shift.department_id}`;

			if (!groupedShifts[key]) {
				groupedShifts[key] = [];
			}
			groupedShifts[key].push(shift);
		});

		// Merge shifts with the same date and time
		const mergedShifts: OpenShiftType[] = Object.values(groupedShifts).map((shifts) => {
			const baseShift = shifts[0];
			const totalNeededNurses = shifts.reduce((sum, s) => sum + s.neededNurses, 0);

			// Convert OpenShiftType[] to OpenShift[] for original_shifts
			const originalShifts: OpenShift[] = shifts.map((s) => ({
				id: s.id,
				created_by: Number(s.created_by),
				request_for: 0,
				shift_type: s.type,
				shift_start: s.startTime,
				shift_end: s.endTime,
				location: s.location,
				needed_nurses: s.neededNurses,
				department_id: s.department_id,
			}));

			const allLocations = [...new Set(shifts.map((s) => s.location).filter(Boolean))];

			return {
				...baseShift,
				id: shifts.map((s) => s.id).join("_"),
				neededNurses: totalNeededNurses,
				is_merged: shifts.length > 1,
				original_shifts: originalShifts,
				locations: allLocations.length > 0 ? allLocations : undefined,
			};
		});

		return mergedShifts;
	}, [staffRequests, userInfo.nurses_info]);

	return (
		<RequestsContext.Provider
			value={{
				insertRequest,
				staffRequests,
				myRequests,
				setStaffRequests,
				mergedRequests,
				refreshRequests,
			}}
		>
			{children}
		</RequestsContext.Provider>
	);
};

export const useRequestsContext = () => {
	const context = useContext(RequestsContext);
	if (!context) throw new Error("useRequests must be used within a RequestsProvider");
	return context;
};
